import { useCallback, useRef } from "react";
import styles from "./SegmentedControl.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

interface SegmentedControlProps {
  activeView: "floor_plan" | "site_plan";
  onViewChange: (view: "floor_plan" | "site_plan") => void;
}

const SegmentedControl = ({ activeView, onViewChange }: SegmentedControlProps) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const { t } = useTranslationHook();

  const options: { value: "floor_plan" | "site_plan"; label: string }[] = [
    { value: "site_plan", label: t("propertypage.floorplanssection.sidenavigation.unitlayout") },
    { value: "floor_plan", label: t("propertypage.floorplanssection.sidenavigation.roomlayout") },
  ];

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onViewChange(options[index].value);
        return;
      }

      let targetIndex: number | null = null;

      if (event.key === "ArrowRight") {
        event.preventDefault();
        targetIndex = index < options.length - 1 ? index + 1 : 0;
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        targetIndex = index > 0 ? index - 1 : options.length - 1;
      } else if (event.key === "Home") {
        event.preventDefault();
        targetIndex = 0;
      } else if (event.key === "End") {
        event.preventDefault();
        targetIndex = options.length - 1;
      }

      if (targetIndex !== null) {
        buttonRefs.current[targetIndex]?.focus();
        onViewChange(options[targetIndex].value);
      }
    },
    [onViewChange],
  );

  return (
    <div
      className={styles.container}
      role="tablist"
      aria-label="View toggle"
      data-testid="floor-plans-segmented-control"
    >
      {options.map((option, index) => {
        const isActive = activeView === option.value;
        return (
          <button
            key={option.value}
            type="button"
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            role="tab"
            aria-selected={isActive}
            className={`${styles.option} ${isActive ? styles.active : ""}`.trim()}
            onClick={() => onViewChange(option.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            tabIndex={isActive ? 0 : -1}
            data-testid={`floor-plans-segment-${option.value}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default wrapperHOC(SegmentedControl, {
  componentName: "SegmentedControl-FloorPlansSection-PropertyPage",
  showForChina: true,
});
